import React, { useState, useCallback } from 'react';
import { Wallet, UserCheck, Bike, CheckCircle, XCircle, Clock, AlertTriangle, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import FilterBar from '../components/FilterBar';
import Modal from '../components/Modal';
import { useFetch, useFilters } from '../hooks';
// import { withdrawRequestsService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import { withdrawService } from "../services/api";



/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', userType: '', status: '', date: '' };

const FILTER_FIELDS = [
  {
    key: 'userType', label: 'User Type', type: 'select',
    options: [{ value: 'Doctor', label: 'Doctor' }, { value: 'Delivery Boy', label: 'Delivery Boy' }],
  },
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'pending',  label: 'Pending'  },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
  { key: 'date', label: 'Date', type: 'date' },
];

const filterFn = (item, f) => {
  if (f.search && !item.userName.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.userType && item.userType !== f.userType) return false;
  if (f.status  && item.status   !== f.status)   return false;
  if (f.date    && item.date     !== f.date)      return false;
  return true;
};

/* ── Status badge ───────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    pending:  { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: Clock },
    approved: { cls: 'bg-green-100  text-green-800  dark:bg-green-900/40  dark:text-green-300',  icon: CheckCircle },
    rejected: { cls: 'bg-red-100    text-red-800    dark:bg-red-900/40    dark:text-red-300',    icon: XCircle },
  };
  const { cls, icon: Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cls}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
};

/* ── Confirmation Modal ─────────────────────────────────────────────────────── */
const ConfirmModal = ({ isOpen, onClose, onConfirm, action, request }) => {
  const isApprove = action === 'approve';
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isApprove ? 'Approve Withdraw Request' : 'Reject Withdraw Request'} size="sm">
      <div className="space-y-4">
        <div className={`flex items-start gap-3 p-4 rounded-xl ${isApprove ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isApprove ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
          <div className="text-sm">
            <p className={`font-semibold ${isApprove ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
              {isApprove ? 'Confirm Approval' : 'Confirm Rejection'}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {isApprove
                ? `This will deduct ${formatCurrency(request?.amount)} from ${request?.userName}'s wallet and mark the request as approved.`
                : `This will reject the withdraw request for ${request?.userName}. No amount will be deducted.`
              }
            </p>
          </div>
        </div>

        {/* Request summary */}
        <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 space-y-2 text-sm">
          {[
            { label: 'Request ID',   value: request?.id },
            { label: 'User',         value: `${request?.userName} (${request?.userType})` },
            { label: 'Amount',       value: formatCurrency(request?.amount) },
            { label: 'Wallet Balance', value: formatCurrency(request?.walletBalance) },
            { label: 'Requested On', value: request?.date ? formatDate(request.date) : '—' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between gap-4">
              <span className="text-gray-500 dark:text-gray-400">{label}</span>
              <span className="font-medium text-gray-900 dark:text-white text-right">{value}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={onConfirm}
            className={`btn-primary ${!isApprove ? '!bg-red-500 hover:!bg-red-600' : ''}`}
          >
            {isApprove ? 'Yes, Approve' : 'Yes, Reject'}
          </button>
        </div>
      </div>
    </Modal>
  );
};



/* ── Page ───────────────────────────────────────────────────────────────────── */
const WithdrawRequests = () => {
  // useFetch me change:
  const {
    data: rawRequests,
    loading,
    refetch,
  } = useFetch(() =>
    withdrawService.getAll().then((list) => list.map(mapWithdrawal)),
  );
  const requests = rawRequests || [];
  const [confirm, setConfirm] = useState(null);
  const [bankTarget, setBankTarget] = useState(null);
  const mapWithdrawal = (w) => ({
    id: w.id,
    amount: parseFloat(w.amount),
    status: w.status === "completed" ? "approved" : w.status,
    // API 'completed' bhejta hai, badge 'approved' dikhata hai
    rejectReason: w.reject_reason,
    date: w.requested_at,
    resolvedAt: w.resolved_at,
    userName: w.doctor_name,
    userType: "Doctor",
    doctorImg: w.doctor_img,
    bankName: w.bank_name,
    beneficiaryName: w.beneficiary_name,
    accountNumber: w.account_number,
    ifscCode: w.ifsc_code,
    walletBalance: w.doctor_wallet,
    // walletBalance API me nahi aata, hide kar sakte ho
  });

  const handleAction = async () => {
    if (!confirm) return;
    const { action, request } = confirm;
    try {
      const status = action === "approve" ? "completed" : "rejected";
      await withdrawService.resolve(
        request.id,
        status,
        action === "reject" ? "Rejected by admin" : "",
      );
      toast.success(
        action === "approve" ? "Request approved" : "Request rejected",
      );
      setConfirm(null);
      refetch();
    } catch (e) {
      toast.error(e.message || "Action failed");
    }
  };

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange, onReset, filtered, activeCount } = useFilters(
    FILTER_INITIAL,
    filterFnCb,
    requests,
  );



  /* Stats */
  const total = requests?.length || 0;
  const pending = requests?.filter((r) => r.status === "pending").length || 0;
  const approved = requests?.filter((r) => r.status === "approved").length || 0;
  const rejected = requests?.filter((r) => r.status === "rejected").length || 0;
  const pendingAmount =
    requests
      ?.filter((r) => r.status === "pending")
      .reduce((s, r) => s + r.amount, 0) || 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Withdraw Requests
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage withdraw requests from doctors and delivery partners
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          {
            label: "Total",
            value: total,
            color: "text-gray-900 dark:text-white",
            bg: "",
          },
          {
            label: "Pending",
            value: pending,
            color: "text-yellow-700 dark:text-yellow-400",
            bg: "bg-yellow-50 dark:bg-yellow-900/10",
          },
          {
            label: "Approved",
            value: approved,
            color: "text-green-700 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/10",
          },
          {
            label: "Rejected",
            value: rejected,
            color: "text-red-600 dark:text-red-400",
            bg: "bg-red-50 dark:bg-red-900/10",
          },
          {
            label: "Pending Amount",
            value: formatCurrency(pendingAmount),
            color: "text-medihub-secondary dark:text-medihub-highlight",
            bg: "bg-medihub-light dark:bg-medihub-dark/10",
          },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onChange}
        onReset={onReset}
        searchPlaceholder="Search by user name…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-medihub-primary" />
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Withdraw Requests ({filtered.length}
              {activeCount ? ` of ${total}` : ""})
            </h3>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                />
              ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Wallet className="w-10 h-10 opacity-30" />
            <p className="text-sm font-medium">No withdraw requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {[
                    "Request ID",
                    "User",
                    "User Type",
                    "Amount",
                    "Status",
                    "Date",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((r) => (
                  <tr
                    key={`${r.userType}-${r.id}`}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    {/* Request ID */}
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {r.id}
                    </td>

                    {/* User */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                            r.userType === "Doctor"
                              ? "bg-medihub-light dark:bg-medihub-dark/40 text-medihub-secondary dark:text-medihub-highlight"
                              : "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300"
                          }`}
                        >
                          {r.userName[0]}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {r.userName}
                        </span>
                      </div>
                    </td>

                    {/* User Type */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          r.userType === "Doctor"
                            ? "bg-medihub-light text-medihub-secondary dark:bg-medihub-dark/40 dark:text-medihub-highlight"
                            : "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300"
                        }`}
                      >
                        {r.userType === "Doctor" ? (
                          <UserCheck className="w-3 h-3" />
                        ) : (
                          <Bike className="w-3 h-3" />
                        )}
                        {r.userType}
                      </span>
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-3 font-bold text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrency(r.amount)}
                    </td>

                    {/* Wallet Balance */}
                    {/* <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`font-semibold ${r.walletBalance >= r.amount ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}
                      >
                        {formatCurrency(r.walletBalance)}
                      </span>
                    </td> */}

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={r.status} />
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(r.date)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setBankTarget(r)}
                          className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-blue-500"
                          title="View Bank Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {r.status === "pending" ? (
                          <>
                            <button
                              onClick={() =>
                                setConfirm({ action: "approve", request: r })
                              }
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Approve
                            </button>
                            <button
                              onClick={() =>
                                setConfirm({ action: "reject", request: r })
                              }
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {r.processedDate
                              ? formatDate(r.processedDate)
                              : "—"}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bank Details Modal */}
      <Modal
        isOpen={!!bankTarget}
        onClose={() => setBankTarget(null)}
        title="Bank Details"
        size="sm"
      >
        <div className="space-y-3">
          <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl text-sm">
            <p className="font-semibold text-gray-900 dark:text-white mb-0.5">
              {bankTarget?.userName}
            </p>
            <p className="text-xs text-gray-500">{bankTarget?.userType}</p>
          </div>
          {[
            { label: "Bank Name", value: bankTarget?.bankName },
            { label: "Beneficiary Name", value: bankTarget?.beneficiaryName },
            { label: "Account Number", value: bankTarget?.accountNumber },
            { label: "IFSC Code", value: bankTarget?.ifscCode },
            { label: "Wallet", value: bankTarget?.walletBalance },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {label}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {value || "—"}
              </span>
            </div>
          ))}
          <button
            onClick={() => setBankTarget(null)}
            className="btn-secondary w-full mt-2"
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleAction}
        action={confirm?.action}
        request={confirm?.request}
      />
    </div>
  );
};;

export default WithdrawRequests;
